import React, { useMemo, useState } from "react";
import { RefreshCw, Database } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const ctl = "h-10 w-full px-3 rounded-md border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring/40";
const uniq = (a) => [...new Set(a.filter(Boolean))].sort();
const when = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");
export const minOf = (hub, r) => (r.min_qty != null ? r.min_qty : hub.default_min_qty || 0);
export const isLow = (hub, r) => minOf(hub, r) > 0 && r.qty < minOf(hub, r);

export const HubBar = ({ hub, onRefresh }) => (
  <div data-testid="stock-hub-bar" className="rounded-lg border border-border bg-card px-4 py-3 text-sm flex flex-wrap items-center gap-x-3 gap-y-1">
    <Database size={14} className={hub.connected ? "text-emerald-700" : "text-muted-foreground"} />
    {hub.connected === null ? <span className="text-muted-foreground">Loading Auto Inventory Hub data…</span>
      : hub.connected ? <span data-testid="stock-hub-counts" className="text-emerald-700 font-medium">Hub connected · {hub.parts.length} parts · {hub.stores.length} stores · {hub.moves.length} Hub entries</span>
        : <span data-testid="stock-hub-error" className="text-destructive">{hub.error}</span>}
    {hub.connected && (hub.stock || []).some((r) => isLow(hub, r)) && <span data-testid="stock-low-count" className="text-xs font-bold uppercase px-2 py-0.5 rounded bg-destructive/15 text-destructive">{(hub.stock || []).filter((r) => isLow(hub, r)).length} low stock</span>}
    {hub.fetched_at && <span className="text-xs text-muted-foreground num">updated {when(hub.fetched_at)}</span>}
    <button data-testid="stock-hub-refresh" onClick={onRefresh} disabled={hub.loading} className="ml-auto h-8 px-3 rounded-md border border-input text-xs flex items-center gap-1.5 hover:bg-muted disabled:opacity-60 transition-colors">
      <RefreshCw size={12} className={hub.loading ? "animate-spin" : ""} /> Refresh
    </button>
  </div>
);

const Filters = ({ s, set, companies, stores }) => (
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
    <select data-testid="cur-stock-company" className={ctl} value={s.company} onChange={(e) => set({ ...s, company: e.target.value })}><option value="">All companies</option>{companies.map((c) => <option key={c}>{c}</option>)}</select>
    <select data-testid="cur-stock-store" className={ctl} value={s.store} onChange={(e) => set({ ...s, store: e.target.value })}><option value="">All stores</option>{stores.map((c) => <option key={c}>{c}</option>)}</select>
    <input data-testid="cur-stock-q" className={ctl} value={s.q} onChange={(e) => set({ ...s, q: e.target.value })} placeholder="Search part number or name" />
  </div>
);

const MinQty = ({ hub, onSaved }) => {
  const [v, setV] = useState(hub.default_min_qty ?? 0);
  const save = async () => {
    try { await api.put("/stock/min-qty", { min_qty: parseFloat(v) || 0 }); toast.success("Low stock minimum saved"); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-muted-foreground">Low stock below</span>
      <input data-testid="stock-min-qty" type="number" min="0" className="h-8 w-20 px-2 rounded-md border border-input bg-card num" value={v} onChange={(e) => setV(e.target.value)} />
      <button data-testid="stock-min-save" onClick={save} className="h-8 px-3 rounded-md bg-primary text-primary-foreground">Save</button>
    </div>
  );
};

export const CurrentStock = ({ hub, admin, onSaved }) => {
  const [s, set] = useState({ company: "", store: "", q: "", low: false });
  const rows = useMemo(() => {
    const q = s.q.trim().toLowerCase();
    return (hub.stock || []).filter((r) => (!s.company || r.company === s.company) && (!s.store || r.store === s.store)
      && (!q || `${r.part_number} ${r.name}`.toLowerCase().includes(q)) && (!s.low || isLow(hub, r)))
      .sort((a, b) => isLow(hub, b) - isLow(hub, a));
  }, [hub, s]);
  if (!hub.connected) return null;
  return (
    <section data-testid="current-stock" className="rounded-xl border border-border bg-card p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base md:text-lg font-semibold">Current stock in Hub</h2>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs"><input data-testid="stock-low-only" type="checkbox" checked={s.low} onChange={(e) => set({ ...s, low: e.target.checked })} /> Low stock only</label>
          {admin && <MinQty hub={hub} onSaved={onSaved} />}
        </div>
      </div>
      <Filters s={s} set={set} companies={uniq((hub.parts || []).map((p) => p.company))} stores={uniq((hub.stores || []).map((x) => x.name))} />
      <div className="max-h-80 overflow-y-auto rounded-md border border-border divide-y divide-border">
        {rows.length === 0 ? <div className="p-6 text-center text-sm text-muted-foreground">No stock found</div> : rows.slice(0, 500).map((r) => (
          <div key={`${r.part_number}-${r.store}`} data-testid={`cur-stock-row-${r.part_number}`} className={`grid grid-cols-[minmax(0,1fr)_minmax(0,0.8fr)_90px] gap-3 px-3 py-2 text-sm ${isLow(hub, r) ? "bg-destructive/5" : ""}`}>
            <div className="min-w-0"><div className="font-medium break-words">{r.part_number}</div><div className="text-[11px] text-muted-foreground break-words">{[r.name, r.company].filter(Boolean).join(" · ")}</div></div>
            <div className="text-xs break-words">{r.store}</div>
            <div className="text-right"><div className={`num font-semibold ${isLow(hub, r) || r.qty <= 0 ? "text-destructive" : ""}`}>{r.qty}</div>
              {isLow(hub, r) && <div data-testid={`low-badge-${r.part_number}`} className="text-[10px] font-bold uppercase text-destructive">Low · min {minOf(hub, r)}</div>}</div>
          </div>
        ))}
      </div>
      {rows.length > 500 && <p className="text-xs text-muted-foreground">Showing 500 of {rows.length}; search to narrow down.</p>}
    </section>
  );
};

export const HubMoves = ({ hub }) => {
  const [q, setQ] = useState("");
  const rows = (hub.moves || []).filter((m) => !q || JSON.stringify(m).toLowerCase().includes(q.toLowerCase()));
  return (
    <section data-testid="hub-moves" className="space-y-3">
      <input data-testid="hub-moves-q" className={ctl} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search Hub entries" />
      <div className="rounded-xl border border-border bg-card divide-y divide-border">
        {!hub.connected ? <div className="p-8 text-center text-sm text-muted-foreground">{hub.error || "Hub not connected"}</div>
          : rows.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground">No stock entries found in the Hub</div>
            : rows.slice(0, 500).map((m) => (
              <div key={m.id} data-testid={`hub-move-${m.id}`} className="grid grid-cols-2 md:grid-cols-[130px_minmax(0,1.3fr)_70px_minmax(0,1.3fr)_minmax(0,1fr)] gap-x-3 gap-y-1 px-4 py-3 text-sm">
                <div><div className="num text-xs">{m.date.replace("T", " ")}</div><div className="text-[11px] text-muted-foreground capitalize">{m.type}</div></div>
                <div className="min-w-0"><div className="font-medium break-words">{m.part_number}</div><div className="text-[11px] text-muted-foreground break-words">{[m.name, m.remark].filter(Boolean).join(" · ")}</div></div>
                <div className="num font-semibold">{m.qty}</div>
                <div className="text-xs break-words">{m.from || m.to ? `${m.from} → ${m.to}` : m.store}</div>
                <div className="text-[11px] text-muted-foreground break-words">{m.by && `By ${m.by}`}</div>
              </div>
            ))}
      </div>
    </section>
  );
};
